// SCR-STAFF-980 — پایش اتاق‌های انتظار مجازی (US-TEL-013، 050؛ AP-TEL-01): اتاق‌های امروز با وضعیت، زمان نوبت، لحظه ورود مراجعه‌کننده به
// اتاق انتظار و نشان «تأخیر ارائه‌دهنده». این صفحه با مجوز General (`tel.session.monitor`) باز می‌شود، پس **هیچ داده بالینی** ندارد: نام
// مراجعه‌کننده و یادداشت‌ها از صفحه نوبت دیده می‌شوند، نه از اینجا. غیبت ۱۵ دقیقه‌ای ارائه‌دهنده را Job دوره‌ای به لغو مرکز و استرداد کامل
// تبدیل می‌کند (BR-TEL-041)، پس این صفحه فقط هشدار می‌دهد و اقدام مالی ندارد.
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type TelVirtualRoomView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useProviders } from '../catalog/common';
import { RoomStatusChip, SCR_STAFF_980, telKeys } from './common';

/** بازه نوسازی فهرست (ثانیه): اتاق انتظار حالت زنده دارد ولی اتصال زنده جدا لازم نیست. */
const REFRESH_MS = 20_000;

export function WaitingRoomPage() {
  const { t } = useTranslation('tel');
  const { tenantApi, session } = useTenantSession();
  const providers = useProviders();
  const [providerId, setProviderId] = useState('');
  const rooms = useQuery({
    queryKey: telKeys.waitingRoom(session?.tenantId, providerId),
    refetchInterval: REFRESH_MS,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/telehealth/waiting-room', {
          params: { query: { providerId: providerId || undefined } },
        }),
      ),
  });
  const columns: DataTableColumn<TelVirtualRoomView>[] = [
    {
      id: 'scheduled',
      headerKey: 'tel:waiting.scheduled',
      cell: (row) => <JalaliDate value={row.scheduledStart} withTime />,
    },
    {
      id: 'provider',
      headerKey: 'tel:waiting.provider',
      cell: (row) => providers.name(row.providerId) || row.providerId.slice(0, 8),
    },
    {
      id: 'appointment',
      headerKey: 'tel:waiting.appointment',
      cell: (row) => (
        <Link
          component={RouterLink}
          to={`/appointments/${row.appointmentId}`}
          data-testid="tel-waiting-appointment"
        >
          {t('tel:waiting.open_appointment')}
        </Link>
      ),
    },
    { id: 'status', headerKey: 'tel:waiting.status', cell: (row) => <RoomStatusChip status={row.status} /> },
    {
      id: 'waiting_since',
      headerKey: 'tel:waiting.waiting_since',
      cell: (row) =>
        row.waitingSince ? (
          <span data-testid="tel-waiting-since">
            <JalaliDate value={row.waitingSince} withTime />
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'identity',
      headerKey: 'tel:waiting.identity',
      cell: (row) => t(`tel:identity.${row.identityVerification}`),
    },
    {
      id: 'late',
      headerKey: 'tel:waiting.provider_late',
      cell: (row) =>
        row.providerLate ? (
          <Chip
            size="small"
            color="warning"
            label={t('tel:waiting.late')}
            data-testid="tel-waiting-late"
          />
        ) : (
          '—'
        ),
    },
    {
      id: 'window',
      headerKey: 'tel:waiting.join_window',
      cell: (row) => <JalaliDate value={row.joinWindowEndsAt} withTime />,
    },
  ];
  const items = rooms.data?.items ?? [];
  const late = items.filter((room) => room.providerLate).length;
  return (
    <ListPage
      screenId={SCR_STAFF_980}
      titleKey="tel:waiting.title"
      subtitleKey="tel:waiting.subtitle"
      badges={
        rooms.data ? (
          <Typography variant="caption" color="text.secondary" data-testid="tel-waiting-as-of">
            {t('tel:waiting.as_of')} <JalaliDate value={rooms.data.asOf} withTime />
          </Typography>
        ) : undefined
      }
      filters={
        <TextField
          select
          size="small"
          label={t('tel:waiting.provider')}
          value={providerId}
          onChange={(event) => setProviderId(event.target.value)}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { 'data-testid': 'tel-waiting-provider-filter' } }}
        >
          <MenuItem value="">{t('tel:common.all')}</MenuItem>
          {providers.options.map((provider) => (
            <MenuItem key={provider.id} value={provider.id}>
              {provider.name}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <Stack spacing={2}>
        {late > 0 ? (
          <Alert severity="warning" data-testid="tel-waiting-late-alert">
            {t('tel:waiting.late_alert', { count: late })}
          </Alert>
        ) : null}
        <DataTable
          captionKey="tel:waiting.title"
          columns={columns}
          rows={rooms.data?.items}
          getRowId={(row) => row.roomId}
          isLoading={rooms.isPending}
          error={rooms.error}
          onRetry={() => void rooms.refetch()}
          emptyTitleKey="tel:waiting.empty"
        />
      </Stack>
    </ListPage>
  );
}
